import { apiClient } from './client'
import type {
  ProviderAccountBindingListResponse,
  ProviderAccountLinkBulkResponse,
  ProviderAccountUnlinkResponse,
  UsageBindingListParams,
  UsageCardListResponse,
} from '@/types/usage'

const isUsageCardListResponse = (
  value: unknown
): value is UsageCardListResponse => {
  if (typeof value !== 'object' || value === null) {
    return false
  }

  return Array.isArray((value as { cards?: unknown }).cards)
}

const isBindingListResponse = (
  value: unknown
): value is ProviderAccountBindingListResponse => {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as { items?: unknown; totalCount?: unknown }
  return (
    Array.isArray(candidate.items) && typeof candidate.totalCount === 'number'
  )
}

export const usage = {
  cards: async (): Promise<UsageCardListResponse> => {
    const response = await apiClient.get<unknown>('/api/v1/usage/cards')
    if (!isUsageCardListResponse(response.data)) {
      throw new Error('The usage service returned an invalid response.')
    }
    return response.data
  },

  bindings: async (
    params: UsageBindingListParams = {}
  ): Promise<ProviderAccountBindingListResponse> => {
    const response = await apiClient.post<unknown>(
      '/api/v1/usage/provider-accounts/search',
      params
    )
    if (!isBindingListResponse(response.data)) {
      throw new Error('The usage binding service returned an invalid response.')
    }
    return response.data
  },

  linkUsers: async (
    providerAccountId: string,
    userIds: string[],
    reason: string
  ): Promise<ProviderAccountLinkBulkResponse> => {
    const response = await apiClient.post<ProviderAccountLinkBulkResponse>(
      `/api/v1/usage/provider-accounts/${providerAccountId}/links/bulk`,
      { userIds, reason }
    )
    return response.data
  },

  unlinkUser: async (
    providerAccountId: string,
    userId: string,
    reason: string
  ): Promise<ProviderAccountUnlinkResponse> => {
    const response = await apiClient.delete<ProviderAccountUnlinkResponse>(
      `/api/v1/usage/provider-accounts/${providerAccountId}/links/${userId}`,
      { data: { reason } }
    )
    return response.data
  },
}
